import { Component, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { FloatLabel } from 'primeng/floatlabel';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { AuthService } from '../../services/auth.service';
import { StorageService } from '../../services/storage.service';
import { passwordMatchValidator } from '../../shared/validators/PasswordMatch';

@Component({
  selector: 'app-restore-password',
  templateUrl: './restore-password.component.html',
  imports: [
    ReactiveFormsModule,
    InputTextModule,
    ButtonModule,
    FloatLabel,
    IconField,
    InputIcon,
  ],
})
export class RestorePasswordComponent {
  passwordErrorMsg =
    'The password must be at least 6 characters long, and contain at least one uppercase letter, one lowercase letter, one digit, and one special character.';
  confirmPasswordErrorMsg = 'Passwords do not match.';

  changePassForm = new FormGroup(
    {
      new_password: new FormControl<string>('', {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.pattern(
            '^(?=.*[A-Z])(?=.*[a-z])(?=.*\\d)(?=.*[-_.,;¿?/¡!@#$%+^&*])[A-Za-z\\d-_.,;¿?/¡!@#$%+^&*]{6,}$'
          ),
        ],
      }),
      confirm_password: new FormControl<string>('', {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.pattern(
            '^(?=.*[A-Z])(?=.*[a-z])(?=.*\\d)(?=.*[-_.,;¿?/¡!@#$%+^&*])[A-Za-z\\d-_.,;¿?/¡!@#$%+^&*]{6,}$'
          ),
        ],
      }),
    },
    { validators: passwordMatchValidator }
  );

  visiblePassword = false;

  private authService = inject(AuthService);

  constructor(
    private router: Router,
    private storageService: StorageService,
    private messageService: MessageService,
    private dialogRef: DynamicDialogRef
  ) {}

  savePass(): void {
    if (this.changePassForm.invalid) {
      this.changePassForm.markAllAsTouched();
      return;
    }

    const token = this.storageService.getItem('token')?.token;
    if (!token) return;

    this.authService
      .changePassword({
        new_password: this.changePassForm.getRawValue().new_password,
        token,
      })
      .subscribe({
        next: (resp) => {
          this.authService.clearPromptNewPassword();
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: resp?.msg || 'Password updated successfully.',
          });
          this.dialogRef.close();
          this.router.navigate(['/dashboard']);
        },
      });
  }

  showPasswordError(): boolean {
    const control = this.changePassForm.get('new_password');
    return !!(
      control &&
      control.touched &&
      (control.hasError('required') || control.hasError('pattern'))
    );
  }

  showConfirmPasswordError(): boolean {
    const control = this.changePassForm.get('confirm_password');
    return !!(
      control &&
      control.touched &&
      control.hasError('passwordMismatch')
    );
  }
}
